import axiosInstance from '../../utils/axiosInstance';
import {
  EmployeeFundResponse,
  EmployeeFundUpdateDto,
  EmployeeFundWithEmployeeResponse,
} from '../../model/EmployeeFund.model';
import {
  EmployeeFundRecord,
  EmployeeFundRecordResponse,
} from '../../model/EmployeeFundRecord.model';

export interface FundTransferDto {
  amount: number;
  description?: string;
  transferDate?: string;
}

export interface FundTransferResponse {
  id: number;
  employee: {
    id: number;
    name: string;
  };
  amount: number;
  transferDate: string;
  description: string;
  status: string;
  employeeBalanceBefore: number;
  employeeBalanceAfter: number;
  companyBalanceBefore: number;
  companyBalanceAfter: number;
  createdAt: string;
  updatedAt: string;
}

class EmployeeFundService {
  private readonly baseURL = '/employee-fund';

  async getFundByEmployeeId(eId: number): Promise<EmployeeFundResponse> {
    try {
      const response = await axiosInstance.get(`${this.baseURL}/${eId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching fund by employee ID:', error);
      throw error;
    }
  }

  async updateFundForEmployee(
    eId: number,
    dto: EmployeeFundUpdateDto
  ): Promise<EmployeeFundResponse> {
    try {
      const response = await axiosInstance.put(
        `${this.baseURL}/update-fund/${eId}`,
        dto
      );
      return response.data;
    } catch (error) {
      console.error('Error updating fund for employee:', error);
      throw error;
    }
  }

  async getAllEmployeeFund(): Promise<EmployeeFundResponse[]> {
    try {
      const response = await axiosInstance.get(this.baseURL);
      return response.data;
    } catch (error) {
      console.error('Error fetching all employee funds:', error);
      throw error;
    }
  }

  async getAllEmployeeFundWithEmployee(): Promise<EmployeeFundWithEmployeeResponse[]> {
    try {
      const response = await axiosInstance.get(`${this.baseURL}/with-employee`);
      return response.data;
    } catch (error) {
      console.error('Error fetching all employee funds with employee:', error);
      throw error;
    }
  }

  // Fund Transfer APIs
  async transferToCompanyAccount(
    employeeId: number,
    transferDto: FundTransferDto
  ): Promise<FundTransferResponse> {
    try {
      const response = await axiosInstance.post(
        `${this.baseURL}/transfer-to-company/${employeeId}`,
        transferDto
      );
      return response.data;
    } catch (error) {
      console.error('Error transferring to company account:', error);
      throw error;
    }
  }

  async editFundTransfer(
    transferId: number,
    newAmount: number,
    newDescription?: string
  ): Promise<FundTransferResponse> {
    try {
      const params = new URLSearchParams();
      params.append('newAmount', newAmount.toString());
      if (newDescription) {
        params.append('newDescription', newDescription);
      }

      const response = await axiosInstance.put(
        `${this.baseURL}/edit-transfer/${transferId}?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      console.error('Error editing fund transfer:', error);
      throw error;
    }
  }

  async cancelFundTransfer(transferId: number): Promise<FundTransferResponse> {
    try {
      const response = await axiosInstance.put(
        `${this.baseURL}/cancel-transfer/${transferId}`
      );
      return response.data;
    } catch (error) {
      console.error('Error cancelling fund transfer:', error);
      throw error;
    }
  }

  async getTransfersByEmployeeId(
    employeeId: number
  ): Promise<FundTransferResponse[]> {
    try {
      const response = await axiosInstance.get(
        `${this.baseURL}/transfers/${employeeId}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching transfers by employee ID:', error);
      throw error;
    }
  }

  async getAllTransfers(): Promise<FundTransferResponse[]> {
    try {
      const response = await axiosInstance.get(`${this.baseURL}/transfers`);
      return response.data;
    } catch (error) {
      console.error('Error fetching all transfers:', error);
      throw error;
    }
  }

  async getTransfersByStatus(status: string): Promise<FundTransferResponse[]> {
    try {
      const response = await axiosInstance.get(
        `${this.baseURL}/transfers/status/${status}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching transfers by status:', error);
      throw error;
    }
  }

  async resetFund(employeeId: number): Promise<void> {
    try {
      await axiosInstance.post(`${this.baseURL}/reset-fund/${employeeId}`);
    } catch (error) {
      console.error('Error resetting fund:', error);
      throw error;
    }
  }

  /**
   * Lấy lịch sử biến động quỹ nhân viên
   */
  static async getEmployeeFundRecords(
    employeeId: number
  ): Promise<EmployeeFundRecord[]> {
    try {
      const response = await axiosInstance.get<EmployeeFundRecord[]>(
        `/employee-fund/employee/${employeeId}/records`
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching employee fund records:', error);
      throw error.response?.data || 'Có lỗi xảy ra khi tải lịch sử biến động';
    }
  }

  /**
   * Lấy lịch sử biến động quỹ nhân viên với phân trang
   */
  static async getEmployeeFundRecordsPaginated(
    employeeId: number,
    page = 0,
    size = 10,
    sortBy = 'dateTime',
    sortDir = 'desc'
  ): Promise<EmployeeFundRecordResponse> {
    try {
      const response = await axiosInstance.get<EmployeeFundRecordResponse>(
        `/employee-fund/employee/${employeeId}/records/paginated`,
        {
          params: { page, size, sortBy, sortDir },
        }
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching paginated employee fund records:', error);
      throw error.response?.data || 'Có lỗi xảy ra khi tải lịch sử biến động';
    }
  }

  /**
   * Lấy lịch sử biến động theo loại
   */
  static async getEmployeeFundRecordsByType(
    employeeId: number,
    changeType: string
  ): Promise<EmployeeFundRecord[]> {
    try {
      const response = await axiosInstance.get<EmployeeFundRecord[]>(
        `/employee-fund/employee/${employeeId}/records/type/${changeType}`
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching employee fund records by type:', error);
      throw error.response?.data || 'Có lỗi xảy ra khi tải lịch sử biến động';
    }
  }
}

export { EmployeeFundService };
export default new EmployeeFundService();
